import { compose, omit } from 'ramda';
import { NOT_FOUND } from 'redux-first-router';

import { authTokenLogin } from '../auth';
import { isServer } from '../utils';
import { redirectTo, toPostLogin } from '../../store/location/actions';
import {
  selectLocation,
  selectParams,
  selectQuery,
} from '../../store/location/selectors';
import {
  sessionRemoval,
  setProspectUser,
  setSessionUser,
} from '../../store/session/actions';
import {
  isAuthenticatedOrProspect,
  isAuthenticated,
} from '../../store/session/selectors';
import { selectTheme } from '../../store/theme/selectors';
import { appendParams } from '../url-builders/url-utils';
import {
  gameUrl,
  loginUrl,
  returnPath as getReturnPath,
  signupUrl,
} from '../url-builders';

/**
 * When a guard function returns `true` a redirect has occurred and the thunk
 * should return early to prevent further processing.
 */

// Auth tokens are single-use - wb-user-service's getUserAndDestroyToken
// destroys the row the moment it's looked up, valid or not - so a `?token=`
// that's already been through authTokenLogin can never succeed again.
// Strips it from the visible URL via history.replaceState (not a real
// navigation, so redux-first-router never sees it and no guard/thunk
// re-runs) so a refresh or a copied/shared link doesn't retry a dead token.
function stripTokenFromUrl() {
  if (isServer) return;

  const url = new URL(window.location.href);
  if (!url.searchParams.has('token')) return;

  url.searchParams.delete('token');
  window.history.replaceState(window.history.state, '', url);
}

export function anonymousOnly(dispatch, getState) {
  const state = getState();

  if (!isAuthenticated(state)) return false;

  const product = selectTheme(state);

  dispatch(toPostLogin(product));

  return true;
}

export function requireAuthentication(dispatch, getState) {
  const state = getState();
  const isLoggedIn = isAuthenticated(state);

  if (isLoggedIn) return false;

  const product = selectTheme(state);
  const { pathname, query } = selectLocation(state);
  const search = new URLSearchParams(omit(['email'], query)).toString();
  const returnPath = getReturnPath({ search, pathname });
  const loginPath = loginUrl({ search });

  compose(
    dispatch,
    redirectTo,
    appendParams({ product, returnPath })
  )(loginPath);

  return true;
}

export function requireAuthOrRedirectToGames(dispatch, getState) {
  const state = getState();

  if (isAuthenticated(state)) return false;

  dispatch(redirectTo('/games'));

  return true;
}

// Same idea as requireAuthOrRedirectToGames, for the GLP Won pay/success
// steps specifically - these always know which game they're for (gameId
// is in the route itself), so a session that's expired/logged out
// (e.g. in another tab) mid-flow sends the visitor back to that exact
// game's own page - which, for a supercharger game, renders the same
// SuperchargerGameSummary/invite UI GameDetailScreenV2 does - never the
// generic /games directory, which says nothing about this specific game.
export function requireAuthOrRedirectToGame(dispatch, getState) {
  const state = getState();

  if (isAuthenticated(state)) return false;

  const { gameId } = selectParams(state);

  dispatch(redirectTo(gameUrl({ gameId })));

  return true;
}

// Same as requireAuthOrRedirectToGame, but first gives a `?token=` in the
// URL a chance to authenticate the visitor before deciding to redirect,
// even when someone is already signed in (signInWithTokenIfPresent) -
// for the GLP Won pay/success steps specifically, reached via the mobile
// app's own "open in hub" link (waybetter-mobile's
// use-navigate-to-supercharger-game.js), which mints this token from an
// already-logged-in app session so the hub visit doesn't hit a login wall.
// Unlike WbMembershipOffer's useAuthTokenAuthentication (prospect-only -
// that token may belong to a visitor who was never a real registered user),
// a token reaching this guard was always minted by a real, already-
// authenticated app account, so a successful redemption sets a full
// session user here, not a prospect one. Kept separate from
// requireAuthOrRedirectToGame rather than changing it in place - every
// other caller of that guard has no token to offer, so this only applies
// where the route itself expects one.
//
// A Supercharger free member (2026-10-08) arrives the same way, from the
// join link in her texts (wb-notify-service's {join_link}), but her token
// is a phone number prospect's, so the session keeps her as one (the
// server's own ping does the same on the next page load). A prospect can't
// stay on these steps (2026-10-16): she's sent to the game's landing page
// to go through the same steps as a paid sign-up.
export async function requireAuthOrTokenOrRedirectToGame(
  dispatch,
  getState,
  ctx
) {
  if (await signInWithTokenIfPresent(dispatch, getState, ctx)) return true;

  const state = getState();
  if (isAuthenticated(state)) return false;

  const { gameId } = selectParams(state);

  // A prospect never pays as one (2026-10-16): like anyone not signed in,
  // she starts at the game's landing page and goes through the steps -
  // Join, her name and number pre-filled, then the account step, which
  // completes this same account (CreateAccount's prospectPhoneNumber).
  dispatch(redirectTo(gameUrl({ gameId })));

  return true;
}

// /glp-won/:gameId/join?token=… (2026-10-15), the join link in a
// Supercharger free member's texts. The link's session always wins: her
// token is exchanged whoever is signed in, and on success her session
// replaces theirs (the auth service's new cookies overwrite the old ones,
// and the funnel's sessionStorage values go with the old session). A dead
// token (expired, or used up) signs nobody in; she still lands on the
// game's page and goes through the steps (2026-10-16, product).
//
// Always redirects, so the token never stays in the address bar, and the
// next page is rendered with her new session cookies (a server-side
// exchange's cookies don't reach this same request's GraphQL calls). It
// goes to the game's landing page (2026-10-16, product): the same checkout
// as a paid sign-up - Join, her name, her number, an account, then pay -
// with her name and number pre-filled from her session. The landing
// page's Join sends a prospect to the name step and a registered user to
// the phone step (SuperchargerGameSummary).
export async function signInFromJoinLink(dispatch, getState, ctx) {
  const state = getState();
  const { gameId } = selectParams(state);
  const { token } = selectQuery(state);

  if (token) {
    const product = selectTheme(state);
    const { user } = await authTokenLogin({ token, product, ctx });

    if (user) {
      dispatch(sessionRemoval());
      dispatch(
        user.role === 'prospect' ? setProspectUser(user) : setSessionUser(user)
      );
    }
  }

  dispatch(redirectTo(gameUrl({ gameId })));

  return true;
}

// Not a redirecting guard like the others here - GameDetailScreenV2 (the
// /games/:gameId page) is public and must stay visible to anonymous
// visitors, so this only ever opportunistically upgrades the session when
// a `?token=` is present (e.g. the app's "open in hub" deep link landing
// here directly, rather than via a /glp-won/:gameId/pay|success step - see
// requireAuthOrTokenOrRedirectToGame above) and returns `false` so
// content-type guard chains (see createRoutesMap's awaitContentThunk) keep
// rendering the page either way - except on the server, where a used
// token always reloads the page without it (see signInWithTokenIfPresent).
export async function authenticateWithTokenIfPresent(dispatch, getState, ctx) {
  return signInWithTokenIfPresent(dispatch, getState, ctx);
}

// A `?token=` always wins over whoever is signed in (2026-10-16): the link
// it came on (the join link in a free member's texts, the app's "open in
// hub" link) is for its own account, so a session left in this browser by
// someone else - or by her own earlier account - mustn't keep it from
// signing her in. The auth service logs the old session out before it
// redeems the token (authenticateToken). On success her session replaces
// it here too, a prospect's as a prospect.
//
// The token leaves the address bar as soon as it's used, worked or not
// (2026-10-16): in the browser stripTokenFromUrl drops it; on the server -
// a fresh page load, where nothing in the browser ever runs this guard
// again - the same page is reloaded without it. That reload also renders
// the page with the new session's cookies, which this request's GraphQL
// calls don't carry yet (as signInFromJoinLink does). Returns true when it
// redirected.
async function signInWithTokenIfPresent(dispatch, getState, ctx) {
  const state = getState();
  const { token } = selectQuery(state);
  if (!token) return false;

  const wasSignedIn = isAuthenticatedOrProspect(state);
  const product = selectTheme(state);
  const { user } = await authTokenLogin({ token, product, ctx });
  stripTokenFromUrl();

  if (user) {
    if (wasSignedIn) dispatch(sessionRemoval());
    dispatch(
      user.role === 'prospect' ? setProspectUser(user) : setSessionUser(user)
    );
  }

  if (!isServer) return false;

  const { pathname, query } = selectLocation(getState());
  dispatch(redirectTo(appendParams(omit(['token'], query || {}))(pathname)));
  return true;
}

export function requireAuthOrProspectEmail(dispatch, getState) {
  const state = getState();

  if (isAuthenticatedOrProspect(state)) return false;

  const product = selectTheme(state);
  const { search, pathname } = selectLocation(state);
  const returnPath = getReturnPath({ search, pathname });
  const signupPath = signupUrl({ search });

  compose(
    dispatch,
    redirectTo,
    appendParams({ product, returnPath })
  )(signupPath);

  return true;
}

// Renders the 404 page at the current URL - the same thing an unknown path
// like /dgfdsgvds gets - unlike toNotFound, which redirects to /not-found.
// meta.notFoundPath keeps the query string, which redux-first-router would
// otherwise drop. Server side, redux-first-router only ever runs the thunk
// of the route the request first matched, so the NOT_FOUND route's own
// thunk (which loads the 404 page's content) has to be called here; client
// side its middleware already does that on dispatch.
export async function showNotFoundInPlace(dispatch, getState) {
  const { pathname, search, query, routesMap } = selectLocation(getState());
  const notFoundPath = search ? `${pathname}?${search}` : pathname;

  dispatch({ type: NOT_FOUND, meta: { notFoundPath, query } });

  if (isServer) await routesMap[NOT_FOUND]?.thunk?.(dispatch, getState);
}
